import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listCustomers,
  createCustomer,
  getCustomer,
  updateCustomer,
  blacklistCustomer,
  listCustomerSubscriptions,
  listCustomerReceipts,
  checkMobileDuplicate,
  enableMobileAccess,
  getCustomer360,
  getKycStatus,
  startKyc,
  deleteKycDocument,
  uploadKycDocument,
  submitKyc,
  markCustomerDeceased,
  customerBanksApi,
  customerNomineesApi,
  customerEducationApi,
  customerOccupationApi,
  customerIntroducersApi,
  customerContactsApi,
  customerAddressesApi,
  listCustomerDocuments,
  uploadCustomerDocument,
  deleteCustomerDocument,
  getKycDocumentTypes,
} from './network';
import type { CreateCustomerPayload, UpdateCustomerPayload } from './types';

const keys = {
  all: ['customers'] as const,
  lists: () => [...keys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...keys.lists(), params] as const,
  details: () => [...keys.all, 'detail'] as const,
  detail: (id: string) => [...keys.details(), id] as const,
  detail360: (id: string) => [...keys.detail(id), '360'] as const,
  subscriptions: (id: string) => [...keys.detail(id), 'subscriptions'] as const,
  receipts: (id: string) => [...keys.detail(id), 'receipts'] as const,
};

export function useCustomers(params: { page: number; limit: number; query?: string }) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => listCustomers(params),
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => getCustomer(id),
    enabled: !!id,
  });
}

export function useCustomer360(id: string) {
  return useQuery({
    queryKey: keys.detail360(id),
    queryFn: () => getCustomer360(id),
    enabled: !!id,
  });
}

export function useCustomerSubscriptions(id: string, params: { page: number; limit: number }) {
  return useQuery({
    queryKey: [...keys.subscriptions(id), params],
    queryFn: () => listCustomerSubscriptions(id, params),
    enabled: !!id,
  });
}

export function useCustomerReceipts(id: string, params: { page: number; limit: number }) {
  return useQuery({
    queryKey: [...keys.receipts(id), params],
    queryFn: () => listCustomerReceipts(id, params),
    enabled: !!id,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCustomerPayload) => createCustomer(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.lists() });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCustomerPayload }) => updateCustomer(id, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: keys.detail(id) });
      queryClient.invalidateQueries({ queryKey: keys.lists() });
    },
  });
}

export function useBlacklistCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => blacklistCustomer(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: keys.detail(id) });
      queryClient.invalidateQueries({ queryKey: keys.lists() });
    },
  });
}

export function useMarkCustomerDeceased() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { deathDate?: string; deathCertificateUrl?: string } }) =>
      markCustomerDeceased(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: keys.detail(id) });
      queryClient.invalidateQueries({ queryKey: keys.lists() });
    },
  });
}

export function checkDuplicateMobile(mobile: string) {
  return checkMobileDuplicate(mobile);
}

export function useEnableMobileAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enableMobileAccess(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: keys.detail(id) });
      queryClient.invalidateQueries({ queryKey: keys.lists() });
    },
  });
}

export function useKycStatus(memberId: string) {
  return useQuery({
    queryKey: ['kyc-status', memberId],
    queryFn: () => getKycStatus(memberId),
    enabled: !!memberId,
  });
}

export function useStartKyc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => startKyc(memberId),
    onSuccess: (_, memberId) => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status', memberId] });
    },
  });
}

export function useDeleteKycDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) => deleteKycDocument(docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status'] });
    },
  });
}

export function useUploadKycDocument(memberId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, docType, docNumber }: { file: File; docType: string; docNumber?: string }) =>
      uploadKycDocument(memberId, file, docType, docNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status', memberId] });
    },
  });
}

export function useSubmitKyc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kycId: string) => submitKyc(kycId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status'] });
    },
  });
}

interface SubEntityApi<TItem, TCreate> {
  list: (customerId: string) => Promise<TItem[]>;
  create: (customerId: string, body: TCreate) => Promise<TItem>;
  remove: (customerId: string, id: string) => Promise<{ success: boolean }>;
}

/**
 * One generic hook factory for the 5 customer child entities (banks/nominees/education/
 * occupation/introducers) -- they share the exact same list/create/delete shape both server-
 * and client-side (see customer-sub-entities.controller.ts and createSubEntityApi in network.ts).
 */
function createSubEntityHooks<TItem, TCreate>(api: SubEntityApi<TItem, TCreate>, queryKeyPrefix: string) {
  return {
    useList: (customerId: string) => useQuery({
      queryKey: [queryKeyPrefix, customerId],
      queryFn: () => api.list(customerId),
      enabled: !!customerId,
    }),
    useCreate: (customerId: string) => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: (body: TCreate) => api.create(customerId, body),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, customerId] });
        },
      });
    },
    useDelete: (customerId: string) => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: (id: string) => api.remove(customerId, id),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, customerId] });
        },
      });
    },
  };
}

export const customerBankHooks = createSubEntityHooks(customerBanksApi, 'customer-banks');
export const customerNomineeHooks = createSubEntityHooks(customerNomineesApi, 'customer-nominees');
export const customerEducationHooks = createSubEntityHooks(customerEducationApi, 'customer-education');
export const customerOccupationHooks = createSubEntityHooks(customerOccupationApi, 'customer-occupation');
export const customerIntroducerHooks = createSubEntityHooks(customerIntroducersApi, 'customer-introducers');
export const customerContactHooks = createSubEntityHooks(customerContactsApi, 'customer-contacts');
export const customerAddressHooks = createSubEntityHooks(customerAddressesApi, 'customer-addresses');

export function useKycDocumentTypes() {
  return useQuery({
    queryKey: ['kyc-document-types'],
    queryFn: () => getKycDocumentTypes(),
  });
}

export function useCustomerDocuments(customerId: string) {
  return useQuery({
    queryKey: ['customer-documents', customerId],
    queryFn: () => listCustomerDocuments(customerId),
    enabled: !!customerId,
  });
}

export function useUploadCustomerDocument(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, docType, docNumber }: { file: File; docType: string; docNumber?: string }) =>
      uploadCustomerDocument(customerId, file, docType, docNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
    },
  });
}

export function useDeleteCustomerDocument(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => deleteCustomerDocument(customerId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
    },
  });
}
